/*
  Glooty product card behaviour. Loaded once per page (defer) from glooty-head, so cards never
  include their own script tag. Custom elements are defined behind a guard, because Dawn's quick
  add and section rendering can bring in markup that loads scripts a second time.

  <glooty-pebbles>  colour sibling pebbles (snippets/glooty-color-siblings.liquid). Pointing at or
                    focusing a pebble previews that colour: in a product card its photo replaces
                    the card photo (data-g-src / data-g-srcset); on the product page the
                    "Color: <name>" label shows its name (data-g-name). Leaving the pebbles puts
                    everything back. Without JS the pebbles are plain links.

  Card photo swipe (phones, grid cards with .g-card__media--swipe): one passive capture listener
  moves the active dot as the photo strip scrolls. Without JS the swipe still works (no dots change).
*/
if (!window.glootyCardSwipe) {
  window.glootyCardSwipe = true;
  document.addEventListener(
    'scroll',
    (event) => {
      const strip = event.target;
      if (!(strip instanceof HTMLElement) || !strip.matches('.g-card__media--swipe .media')) return;
      const index = Math.round(strip.scrollLeft / Math.max(strip.clientWidth, 1));
      const dots = strip.closest('.g-card__media--swipe').querySelectorAll('.g-card__dot');
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    },
    { capture: true, passive: true }
  );
}

if (!customElements.get('glooty-pebbles')) {
  class GlootyPebbles extends HTMLElement {
    connectedCallback() {
      this.label = this.querySelector('[data-g-pebbles-name]');
      this.card = this.closest('.g-card');
      this.image = this.card ? this.card.querySelector('.card__media img') : null;
      if (!this.label && !this.image) return;

      if (this.label) this.labelText = this.label.textContent;
      if (this.image) {
        this.imageSrc = this.image.getAttribute('src');
        this.imageSrcset = this.image.getAttribute('srcset');
      }

      this.onEnter = this.onEnter.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.restore = this.restore.bind(this);
      this.addEventListener('pointerover', this.onEnter);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('pointerleave', this.restore);
      this.addEventListener('focusout', this.onFocusOut);
    }

    disconnectedCallback() {
      if (!this.onEnter) return;
      this.removeEventListener('pointerover', this.onEnter);
      this.removeEventListener('focusin', this.onEnter);
      this.removeEventListener('pointerleave', this.restore);
      this.removeEventListener('focusout', this.onFocusOut);
    }

    onEnter(event) {
      const pebble = event.target.closest('a.g-pebble');
      if (pebble && this.contains(pebble)) this.preview(pebble);
    }

    onFocusOut(event) {
      if (!this.contains(event.relatedTarget)) this.restore();
    }

    preview(pebble) {
      if (this.label) this.label.textContent = pebble.dataset.gName || this.labelText;
      if (!this.image) return;
      const src = pebble.dataset.gSrc;
      if (!src) {
        this.restoreImage();
        return;
      }
      if (pebble.dataset.gSrcset) this.image.setAttribute('srcset', pebble.dataset.gSrcset);
      else this.image.removeAttribute('srcset');
      this.image.setAttribute('src', src);
      this.card.classList.add('is-previewing');
    }

    restoreImage() {
      if (!this.image || !this.card.classList.contains('is-previewing')) return;
      if (this.imageSrcset) this.image.setAttribute('srcset', this.imageSrcset);
      else this.image.removeAttribute('srcset');
      this.image.setAttribute('src', this.imageSrc);
      this.card.classList.remove('is-previewing');
    }

    restore() {
      if (this.label) this.label.textContent = this.labelText;
      this.restoreImage();
    }
  }

  customElements.define('glooty-pebbles', GlootyPebbles);
}
